import { useMemo, useState } from 'react'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { SourceAttributionBadge } from '@/components/catalog/source-attribution-badge'
import { CartPreviewDialog } from '@/components/checkout/cart-preview-dialog'
import { OrderAtSourceButton } from '@/components/checkout/order-at-source-button'
import { groupCartItemsBySource } from '@/features/checkout/cart'
import { formatUsd, rollupBomFromSteps, type BomMaterialLine } from '@/features/projects/rollup-bom'
import type { FollowingDetail } from '@/api/types'

interface PersonalizedBomPanelProps {
  following: FollowingDetail
  variant?: 'card' | 'plain'
}

const formatQty = (qty: number): string => {
  if (Number.isInteger(qty)) return String(qty)
  return qty.toFixed(2).replace(/\.?0+$/, '')
}

const renderMaterialLine = (line: BomMaterialLine) => {
  const ownedFraction = line.ownedQty > 0
  const fullyOwned = line.neededQty === 0
  return (
    <li key={line.catalogMaterialId} className="flex flex-col gap-0.5 py-1">
      <div className="flex items-baseline justify-between gap-3">
        <span className="flex flex-1 items-center gap-2 truncate">
          <span className="truncate">{line.name}</span>
          {line.priceTier !== 'default' && (
            <SourceAttributionBadge
              tier={line.priceTier}
              liveSource={line.liveSource}
              isStale={line.isLivePriceStale}
            />
          )}
        </span>
        <span className="w-20 text-right tabular-nums">
          {fullyOwned ? '—' : formatUsd(line.lineTotalCents)}
        </span>
      </div>
      <div className="flex items-baseline justify-between gap-3 text-xs text-muted-foreground">
        {ownedFraction ? (
          <span>
            need {formatQty(line.neededQty)} {line.unit} (own {formatQty(line.ownedQty)} of{' '}
            {formatQty(line.totalQty)})
          </span>
        ) : (
          <span>
            need {formatQty(line.totalQty)} {line.unit} × {formatUsd(line.unitPriceCents)}
          </span>
        )}
      </div>
    </li>
  )
}

/**
 * Personalized BOM for the doing view: same shape as the editor's {@link BomPanel} but matches
 * Inventory and shows owned/needed per line. Pricing is Live (catalog comes from the source's
 * current state on every fetch).
 */
export const PersonalizedBomPanel = ({
  following,
  variant = 'card',
}: PersonalizedBomPanelProps) => {
  const rollup = useMemo(
    () => rollupBomFromSteps(following.snapshotSteps, following.catalog, following.inventory),
    [following.snapshotSteps, following.catalog, following.inventory]
  )
  const isEmpty = rollup.materials.length === 0 && rollup.tools.length === 0
  const cartGroups = useMemo(
    () => groupCartItemsBySource([...rollup.materials, ...rollup.tools]),
    [rollup.materials, rollup.tools]
  )
  const [openCartSlug, setOpenCartSlug] = useState<string | null>(null)
  const openGroup = openCartSlug
    ? (cartGroups.find((g) => g.sourceSlug === openCartSlug) ?? null)
    : null

  const body = (
    <div className="space-y-5">
      <section>
        <h3 className="text-xs font-medium uppercase tracking-wide text-muted-foreground">
          Materials
        </h3>
        {rollup.materials.length === 0 ? (
          <p className="mt-1 text-sm text-muted-foreground">None.</p>
        ) : (
          <ul className="mt-2 divide-y divide-border text-sm">
            {rollup.materials.map(renderMaterialLine)}
          </ul>
        )}
      </section>

      <section>
        <h3 className="text-xs font-medium uppercase tracking-wide text-muted-foreground">Tools</h3>
        {rollup.tools.length === 0 ? (
          <p className="mt-1 text-sm text-muted-foreground">None.</p>
        ) : (
          <ul className="mt-2 space-y-1 text-sm">
            {rollup.tools.map((line) => (
              <li key={line.catalogToolId} className="flex items-baseline justify-between gap-3">
                <span className="flex flex-1 items-center gap-2 truncate">
                  <span className="truncate">{line.name}</span>
                  {line.owned && <span className="text-xs text-muted-foreground">owned</span>}
                  {line.priceTier !== 'default' && (
                    <SourceAttributionBadge
                      tier={line.priceTier}
                      liveSource={line.liveSource}
                      isStale={line.isLivePriceStale}
                    />
                  )}
                </span>
                <span className="w-20 text-right tabular-nums">
                  {line.owned ? '—' : formatUsd(line.unitPriceCents)}
                </span>
              </li>
            ))}
          </ul>
        )}
      </section>

      <div className="h-px w-full bg-border" />

      <div className="flex items-baseline justify-between text-sm">
        <span className="font-medium">Still to buy</span>
        <span className="text-base font-semibold tabular-nums">
          {isEmpty ? 'Nothing to total yet.' : formatUsd(rollup.grandTotalCents)}
        </span>
      </div>

      {cartGroups.length > 0 && (
        <div className="flex flex-wrap gap-2 border-t pt-3">
          {cartGroups.map((group) => (
            <OrderAtSourceButton
              key={group.sourceSlug}
              sourceDisplayName={group.sourceDisplayName}
              itemCount={group.items.length}
              subtotalCents={group.subtotalCents}
              onClick={() => setOpenCartSlug(group.sourceSlug)}
            />
          ))}
        </div>
      )}
      {openGroup && (
        <CartPreviewDialog
          group={openGroup}
          open={!!openCartSlug}
          onOpenChange={(o) => !o && setOpenCartSlug(null)}
        />
      )}
    </div>
  )

  if (variant === 'plain') return body

  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-lg">Personalized BOM</CardTitle>
      </CardHeader>
      <CardContent>{body}</CardContent>
    </Card>
  )
}
